import { Controller } from '@hotwired/stimulus'
import createGlobe from 'cobe'

const PROJECT_LOCATIONS = [
  { id: 'mohegan-lake', location: [41.32, -73.85], dot: 10 }, // Mohegan Lake, NY
  { id: 'pleasantville', location: [41.14, -73.79], dot: 10 }, // Pleasantville, NY
  { id: 'yonkers', location: [40.93, -73.9], dot: 10 }, // Yonkers, NY
  { id: 'accra', location: [5.6, -0.19], dot: 14 }, // Accra, Ghana
  { id: 'banjul', location: [13.45, -16.58], dot: 14 }, // Banjul, The Gambia
  { id: 'dakar', location: [14.72, -17.47], dot: 14 }, // Dakar, Senegal
  { id: 'dar-es-salaam', location: [-6.79, 39.21], dot: 14 }, // Dar es Salaam, Tanzania
]

const GLOBE_RADIUS = 0.8
const MARKER_ELEVATION = 0.01
const THETA = 0.2

// Convert latitude and longitude to a 3D point on the globe surface.
// Mirrors cobe's own projection so fallback positioning matches the render.
function latLonTo3D([lat, lon]) {
  const latRad = (lat * Math.PI) / 180
  const lonRad = (lon * Math.PI) / 180 - Math.PI
  const r = GLOBE_RADIUS + MARKER_ELEVATION
  return [
    -Math.cos(latRad) * Math.cos(lonRad) * r,
    Math.sin(latRad) * r,
    Math.cos(latRad) * Math.sin(lonRad) * r,
  ]
}

// Project a 3D point to fractional canvas coordinates for a given rotation.
function project([px, py, pz], phi) {
  const cx = Math.cos(THETA)
  const sx = Math.sin(THETA)
  const cy = Math.cos(phi)
  const sy = Math.sin(phi)

  const rx = cy * px + sy * pz
  const ry = sy * sx * px + cx * py - cy * sx * pz
  const rz = -sy * cx * px + sx * py + cy * cx * pz

  return {
    x: (rx + 1) / 2,
    y: (-ry + 1) / 2,
    visible: rz >= 0 || rx * rx + ry * ry >= GLOBE_RADIUS * GLOBE_RADIUS,
  }
}

// A slowly rotating globe with a marker for each project location.
// The globe configuration copies the hero globe on https://cobe.vercel.app.
// Markers are HTML dots instead of WebGL dots, because WebGL markers vanish
// abruptly at the horizon, and a CSS opacity transition eases them in and out.
// Where the browser supports CSS anchor positioning, the dots attach to cobe's
// anchors and fade through --cobe-visible-<id>. Elsewhere the controller
// projects each location itself and positions the dots every frame.
export default class extends Controller {
  static targets = ['canvas', 'polaroid']

  connect() {
    this.phi = 5.5
    this.useAnchors = CSS.supports('anchor-name: --a')
    this.build()

    // Rebuild when the canvas is resized or first becomes visible. A canvas
    // hidden at load measures 0 wide and cannot host a globe until it shows.
    this.lastWidth = this.canvasTarget.offsetWidth
    this.observer = new ResizeObserver(() => {
      const width = this.canvasTarget.offsetWidth
      if (width !== this.lastWidth) {
        this.lastWidth = width
        this.build()
      }
    })
    this.observer.observe(this.canvasTarget)
  }

  build() {
    cancelAnimationFrame(this.frame)
    this.globe?.destroy()
    this.globe = null
    const size = this.canvasTarget.offsetWidth
    if (size === 0) return
    const width = size * 2
    const dpr = Math.min(
      window.devicePixelRatio || 1,
      window.innerWidth < 640 ? 1.8 : 2,
    )

    this.globe = createGlobe(this.canvasTarget, {
      devicePixelRatio: dpr,
      width: width,
      height: width,
      phi: this.phi,
      theta: THETA,
      dark: 0,
      diffuse: 1.5,
      mapSamples: 16000,
      mapBrightness: 10,
      baseColor: [1, 1, 1],
      markerColor: [0.3, 0.45, 0.85],
      glowColor: [0.94, 0.93, 0.91],
      markerElevation: MARKER_ELEVATION,
      markers: PROJECT_LOCATIONS.map((m) => ({ ...m, size: 0 })),
      opacity: 0.7,
    })

    this.dots = PROJECT_LOCATIONS.map((m) => {
      const dot = document.createElement('div')
      const position = this.useAnchors
        ? `position-anchor:--cobe-${m.id};top:anchor(center);left:anchor(center);` +
          `opacity:var(--cobe-visible-${m.id}, 0);`
        : 'opacity:0;'
      dot.style.cssText =
        `position:absolute;${position}translate:-50% -50%;` +
        `width:${m.dot}px;height:${m.dot}px;border-radius:50%;` +
        'background:#e07508;pointer-events:none;transition:opacity 0.4s'
      this.element.append(dot)
      return dot
    })

    if (!this.useAnchors) this.points = PROJECT_LOCATIONS.map((m) => latLonTo3D(m.location))

    // Advance by elapsed time, not by frame, so the speed is the same at any
    // display refresh rate. 0.18 radians per second matches 0.003 per frame
    // on a 60 Hz display.
    let last = performance.now()
    const animate = (now) => {
      this.phi += 0.18 * (Math.min(now - last, 100) / 1000)
      last = now
      this.globe.update({ phi: this.phi })
      if (!this.useAnchors) this.positionDots()
      this.frame = requestAnimationFrame(animate)
    }
    animate(last)

    setTimeout(() => (this.canvasTarget.style.opacity = '1'))
  }

  disconnect() {
    cancelAnimationFrame(this.frame)
    this.globe?.destroy()
    this.dots?.forEach((dot) => dot.remove())
    this.canvasTarget.style.opacity = '0'
  }

  positionDots() {
    this.points.forEach((point, i) => {
      const { x, y, visible } = project(point, this.phi)
      const dot = this.dots[i].style
      dot.left = `${x * 100}%`
      dot.top = `${y * 100}%`
      dot.opacity = visible ? '1' : '0'
    })

    this.polaroidTargets.forEach((polaroid) => {
      const location = PROJECT_LOCATIONS.find(
        (m) => m.id === polaroid.dataset.globeId,
      )?.location
      if (!location) return
      const { x, y, visible } = project(latLonTo3D(location), this.phi)
      polaroid.style.left = `${x * 100}%`
      polaroid.style.top = `${y * 100}%`
      polaroid.style.translate = '-50% calc(-100% - 8px)'
      polaroid.style.opacity = visible ? '1' : '0'
      polaroid.style.filter = visible ? 'none' : 'blur(8px)'
    })
  }
}
